import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { homeFor, OWNER_ROOT, PORTAL_ROOT, PROVIDER_ROOT } from '../lib/portal';

/** Gate for the authenticated app. Unauthenticated users are sent to sign-in,
 *  remembering where they were headed so they land there after logging in. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, roles } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  /*
   * Somebody whose home is a portal of their own belongs there, not here.
   *
   * Convenience rather than the security boundary — the server refuses every
   * landlord endpoint to a tenant_user or owner_user regardless, and row-level
   * security refuses the rows underneath even if a route were miswired. What
   * this stops is the softer failure: someone who follows an old link or types
   * "/" gets their own portal instead of a landlord shell in which every band
   * on every page reports an error.
   *
   * Asked through homeFor rather than by naming the roles, because naming them
   * is what broke it: this said `roles.includes('tenant_user')`, the Owner
   * Portal arrived, and an owner signing in landed on the landlord dashboard
   * with an empty sidebar and "You do not have access to this" where the page
   * should be. A portal role added later is handled here the moment homeFor
   * knows about it.
   */
  const home = homeFor(roles);
  if (home === PORTAL_ROOT || home === OWNER_ROOT || home === PROVIDER_ROOT) {
    return <Navigate to={home} replace />;
  }
  return <>{children}</>;
}
